import { notification } from 'antd';

import type { Settings } from '@ant-design/pro-components';

import { AxiosError, history, RequestConfig, RunTimeLayoutConfig, SelectLang } from '@umijs/max';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';

import Logo from '@/components/Logo';
import Main from '@/components/Main';
import ThresholdSwitch from '@/components/ThresholdSwitch';
import UserDropdown from '@/components/UserDropdown';

import defaultSettings from '../config/defaultSettings';
import NotFound from './pages/Exceptions/NotFound';
import Unauthorized from './pages/Exceptions/Unauthorized';
import { getUser } from './services';
import { InitialState } from './types';
import { identifyClarityUser, initClarity, staticIntl } from './utils';

initClarity();

import 'dayjs/locale/en';
import 'dayjs/locale/pl';

dayjs.extend(utc);
dayjs.extend(localizedFormat);

const LOGIN_PATH = '/login';

export async function getInitialState(): Promise<InitialState> {
  const settings = defaultSettings as Settings;

  try {
    const userId = localStorage.getItem('userId');

    if (!userId) {
      history.push(LOGIN_PATH);
      return { settings };
    }

    const currentUser = await getUser({ id: userId });

    identifyClarityUser(currentUser.id);

    return {
      currentUser,
      settings,
    };
  }
  catch {
    history.push(LOGIN_PATH);
    return { settings };
  }
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => ({
  actionsRender: () => [<ThresholdSwitch key="ThresholdSelect" />, <SelectLang key="SelectLang" />],
  avatarProps: {
    render: () => <UserDropdown />,
  },
  childrenRender: dom => (<Main>{dom}</Main>),
  logo: <Logo />,
  noFound: <NotFound />,
  onPageChange: () => {
    const { location } = history;
    if (!initialState?.currentUser && location.pathname !== LOGIN_PATH) {
      history.push(LOGIN_PATH);
    }
  },
  title: '',
  unAccessible: <Unauthorized />,
  ...initialState?.settings,
});

export const request: RequestConfig = {
  baseURL: process.env.API_URL,
  errorConfig: {
    errorHandler: (error) => {
      if ((error as AxiosError).response?.status === 401) {
        history.push(LOGIN_PATH);

        const intl = staticIntl();

        notification.open({
          description: intl.formatMessage({ id: 'unauthorized.description' }),
          message: intl.formatMessage({ id: 'unauthorized.title' }),
          pauseOnHover: true,
          showProgress: true,
          type: 'error',
        });
      }
    },
  },
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
};
